import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import * as THREE from "three/webgpu";
import {PointsMaterialFactory} from '../material';
import {SketchPointKind, SketchPointObject} from '../mesh/sketch';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {SketchDrawingTool} from './DrawingSharedTypes';
import {BehaviorSubject, type Observable} from 'rxjs';

/**
 * 点绘制命令枚举。
 */
export enum SketchPointCommand {
    /** 点命令。 */
    Point = 'sketch-point',
}

/**
 * 判断命令是否为点绘制命令。
 * @param command 命令字符串。
 * @returns 是否为点绘制命令。
 */
export function isSketchPointCommand(command: string): command is SketchPointCommand {
    return Object.values(SketchPointCommand).includes(command as SketchPointCommand);
}


/**
 * 点绘制时的实时坐标快照。
 */
export interface PointParametersSnapshot {
    /** 参考面局部 X 坐标。 */
    x: number | null;
    /** 参考面局部 Y 坐标。 */
    y: number | null;
}

/**
 * 草图点绘制处理器。
 * 说明：
 * 1. 负责实现旧版草图点绘制完整流程；
 * 2. 坐标解析优先级：上游点捕捉结果 -> 绘图平面拾取；
 * 3. 失败时仅上报并终止，不提交点实体。
 */
export class SketchPointDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Point;

    /** 绘制过程中实时坐标流。 */
    private readonly pointParametersSubject = new BehaviorSubject<PointParametersSnapshot>({ x: null, y: null });

    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    
    getRequiredPointCount(): number {
        return 1;
    }

    
    get closable(): boolean {
        return false;
    }

    
    getDrawingHint(): string {
        return '单击已放置点 | ESC取消';
    }

    
    resetState(): void {
        this.drawingPoints = [];
        // 重置坐标流为空值。
        this.pointParametersSubject.next({ x: null, y: null });
    }

    /**
     * @description 监听绘制过程中实时坐标变化（鼠标移动时推送参考面局部坐标）。
     * @returns 点坐标参数只读流。
     */
    public observePointParameters(): Observable<PointParametersSnapshot> {
        return this.pointParametersSubject.asObservable();
    }


    /**
     * 处理点绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。
     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload,): void {

        // 与线/圆保持一致：当上游已经解析出 snappedPoint 时，
        // 点实体必须直接落在捕捉点，而不是重新取一次原始鼠标落面点。
        const position = this.resolvePointDrawingPosition(payload);

        // 3. 创建点实体并清理交互状态。
        if (position){
            this.createPointByHandler(position);
            this.cleanupPointDrawingInteractionStateByHandler();
        }
    }


    /**
     * 在处理器内创建草图点实体。
     * @param position 草图点坐标。
     */
    private createPointByHandler(position: THREE.Vector3): void {
        const point = new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.SketchPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane: this.referencePlane
        });
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(point);
        this.viewer.addSketchObject(point);
    }
    /**
     * 鼠标移动时更新预览坐标，通过参数流推送当前参考面局部坐标。
     * @param currentPoint 当前鼠标在 3D 空间中的投影点。
     */
    updatePreview(currentPoint: THREE.Vector3): void {
        // 将世界坐标转换为参考面局部 2D 坐标。
        const local = this.referencePlane.worldToLocal(currentPoint);
        this.pointParametersSubject.next({ x: local.x, y: local.y });
    }

    /**
     * 在处理器内清理点绘制交互状态。
     */
    private cleanupPointDrawingInteractionStateByHandler(): void {
        this.cleanupDrawingState({
        });
    }

    /**
     * 解析点绘制当前点击点。
     * 说明：
     * 1. 优先使用绘制管理器预先注入的 snappedPoint；
     * 2. 无捕捉结果时再回退到鼠标原始落面点；
     * 3. 统一在此方法收口点绘制点击点来源，避免输入优先级分散。
     * @param payload 鼠标按下载荷。
     * @returns 本次点击最终采用的世界坐标。
     */
    private resolvePointDrawingPosition(payload: SketchDrawingMouseDownPayload): THREE.Vector3 {
        return payload.snappedPoint ?? this.getDrawingPoint(payload.event);
    }

}

